/**
 * Code-source setup links (FW-32): a manager hands "Connect a code source"
 * to a developer without a Repoworks account.
 *
 * The inviter's hooks are ordinary org-scoped queries. The developer's
 * calls carry the scoped session in ``X-Setup-Session`` instead of the
 * sign-in cookie; the backend accepts that header on ``/setup-links/*``
 * only.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type { CodeSourceKind, CodeSourceView, ConnectionTestResult } from "@/api/codeSources";

export type SetupLinkStatus = "active" | "used" | "expired" | "revoked";

export type SetupLinkView = {
  id: string;
  scm_kind: CodeSourceKind | null;
  status: SetupLinkStatus;
  recipient_email: string | null;
  allow_write: boolean;
  created_at: string;
  expires_at: string;
  used_at: string | null;
  revoked_at: string | null;
  created_by_name: string | null;
  code_source_id: string | null;
  code_source_name: string | null;
  /** Only on the create response: the link, shown once. */
  link?: string | null;
  emailed?: boolean | null;
  email_configured?: boolean | null;
};

/** The lifetimes the inviter can pick, in hours (the backend's list). */
export const SETUP_LINK_LIFETIMES = [24, 72, 168] as const;
export type SetupLinkLifetime = (typeof SETUP_LINK_LIFETIMES)[number];

export const setupLinksKey = (slug: string) => ["orgs", slug, "code-source-setup-links"] as const;

export function useSetupLinks(slug: string, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: setupLinksKey(slug),
    queryFn: async () =>
      (await api.get<SetupLinkView[]>(`/orgs/${slug}/code-source-setup-links`)).data,
    enabled: Boolean(slug) && (options.enabled ?? true),
    staleTime: 10_000,
    // While a link is out, look again now and then: the inviter sees the
    // developer's source arrive without reloading.
    refetchInterval: (query) =>
      (query.state.data ?? []).some((l) => l.status === "active") ? 15_000 : false,
  });
}

export function useCreateSetupLink(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      scm_kind: CodeSourceKind | null;
      lifetime_hours: SetupLinkLifetime;
      email?: string | null;
      note?: string | null;
      allow_write?: boolean;
    }) =>
      (await api.post<SetupLinkView>(`/orgs/${slug}/code-source-setup-links`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: setupLinksKey(slug) }),
  });
}

export function useRevokeSetupLink(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/orgs/${slug}/code-source-setup-links/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: setupLinksKey(slug) }),
  });
}

// ---- The developer's side --------------------------------------------------

export type SetupSessionView = {
  org_name: string;
  /** The inviter's own words (name, note): render as plain text only. */
  inviter_name: string | null;
  inviter_email: string | null;
  /** The inviter's UI language: the page's fallback when the browser's
   *  languages aren't offered. */
  inviter_language: string | null;
  note: string | null;
  /** Whether the inviter allowed a write token too. */
  allow_write: boolean;
  scm_kind: CodeSourceKind | null;
  link_expires_at: string;
  session_expires_at: string;
  source: CodeSourceView | null;
  /** Only from ``open``. */
  session?: string | null;
};

export type SetupSourcePayload = {
  display_name?: string;
  kind?: CodeSourceKind;
  base_url?: string;
  group_filter?: string | null;
  ssh_host?: string | null;
  pat_read?: string | null;
  pat_write?: string | null;
};

const SESSION_HEADER = "X-Setup-Session";
const scoped = (session: string) => ({ headers: { [SESSION_HEADER]: session } });

export const setupSessionApi = {
  open: async (token: string): Promise<SetupSessionView> =>
    (await api.post<SetupSessionView>("/setup-links/open", { token })).data,
  get: async (session: string): Promise<SetupSessionView> =>
    (await api.get<SetupSessionView>("/setup-links/session", scoped(session))).data,
  create: async (session: string, payload: SetupSourcePayload): Promise<CodeSourceView> =>
    (await api.post<CodeSourceView>("/setup-links/session/code-source", payload, scoped(session)))
      .data,
  update: async (session: string, payload: SetupSourcePayload): Promise<CodeSourceView> =>
    (await api.patch<CodeSourceView>("/setup-links/session/code-source", payload, scoped(session)))
      .data,
  test: async (session: string): Promise<ConnectionTestResult> =>
    (
      await api.post<ConnectionTestResult>(
        "/setup-links/session/code-source/test",
        undefined,
        scoped(session),
      )
    ).data,
};
